import { createTheme } from '@mui/material/styles';

// Dark enterprise color palette
const colors = {
  primary: {
    main: '#16a34a',
    light: '#22c55e',
    dark: '#15803d',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#3b82f6',
    light: '#60a5fa',
    dark: '#2563eb',
    contrastText: '#ffffff',
  },
  success: {
    main: '#22c55e',
    light: '#4ade80',
    dark: '#16a34a',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#f59e0b',
    light: '#fbbf24',
    dark: '#d97706',
    contrastText: '#000000',
  },
  error: {
    main: '#ef4444',
    light: '#f87171',
    dark: '#dc2626',
    contrastText: '#ffffff',
  },
  info: {
    main: '#3b82f6',
    light: '#60a5fa',
    dark: '#2563eb',
    contrastText: '#ffffff',
  },
  grey: {
    50: '#1e1e1e',
    100: '#1a1a1a',
    200: '#2a2a2a',
    300: '#333333',
    400: '#444444',
    500: '#666666',
    600: '#888888',
    700: '#a0a0a0',
    800: '#cccccc',
    900: '#e5e5e5',
  },
  background: {
    default: '#0a0f0a',
    paper: '#141414',
  },
  text: {
    primary: '#e5e5e5',
    secondary: '#a0a0a0',
    disabled: '#666666',
  },
  divider: '#1a1a1a',
  action: {
    active: '#a0a0a0',
    hover: 'rgba(22, 163, 74, 0.08)',
    selected: 'rgba(22, 163, 74, 0.12)',
    disabled: '#444444',
    disabledBackground: '#1a1a1a',
  },
};

// Chart colors for dark backgrounds
export const chartColors = {
  green: '#16a34a',
  blue: '#3b82f6',
  amber: '#f59e0b',
  red: '#ef4444',
  purple: '#a855f7',
  cyan: '#06b6d4',
  emerald: '#22c55e',
  orange: '#f97316',
  grid: '#1a1a1a',
  tooltipBg: '#1e1e1e',
  tooltipBorder: '#2a2a2a',
  tooltipText: '#e5e5e5',
};

// Create the theme
export const theme = createTheme({
  palette: {
    mode: 'dark',
    ...colors,
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    h1: {
      fontFamily: '"JetBrains Mono", monospace',
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      color: colors.text.primary,
    },
    h2: {
      fontFamily: '"JetBrains Mono", monospace',
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.3,
      color: colors.text.primary,
    },
    h3: {
      fontFamily: '"JetBrains Mono", monospace',
      fontSize: '1.35rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: colors.text.primary,
    },
    h4: {
      fontFamily: '"JetBrains Mono", monospace',
      fontSize: '1.1rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: colors.text.primary,
    },
    h5: {
      fontFamily: '"JetBrains Mono", monospace',
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: colors.text.primary,
    },
    h6: {
      fontFamily: '"JetBrains Mono", monospace',
      fontSize: '0.9rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: colors.text.primary,
    },
    body1: {
      fontSize: '0.9rem',
      fontWeight: 400,
      lineHeight: 1.6,
      color: colors.text.primary,
    },
    body2: {
      fontSize: '0.85rem',
      fontWeight: 400,
      lineHeight: 1.6,
      color: colors.text.secondary,
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 500,
      lineHeight: 1.4,
      color: colors.text.secondary,
    },
    button: {
      fontSize: '0.85rem',
      fontWeight: 600,
      textTransform: 'none',
    },
    overline: {
      fontFamily: '"JetBrains Mono", monospace',
      fontSize: '0.7rem',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: colors.text.secondary,
    },
  },
  shape: {
    borderRadius: 4,
  },
  spacing: 8,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.background.default,
          color: colors.text.primary,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: 'none',
          fontWeight: 600,
          padding: '6px 14px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: 'none',
          },
        },
        outlined: {
          borderColor: '#2a2a2a',
          '&:hover': {
            borderColor: '#16a34a',
            backgroundColor: 'rgba(22, 163, 74, 0.08)',
          },
        },
        text: {
          '&:hover': {
            backgroundColor: 'rgba(22, 163, 74, 0.08)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          boxShadow: 'none',
          border: '1px solid #1a1a1a',
          backgroundColor: '#141414',
          '&:hover': {
            borderColor: '#2a2a2a',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundImage: 'none',
          backgroundColor: '#141414',
        },
        elevation0: {
          boxShadow: 'none',
        },
        elevation1: {
          boxShadow: 'none',
          border: '1px solid #1a1a1a',
        },
        elevation2: {
          boxShadow: 'none',
          border: '1px solid #2a2a2a',
        },
        elevation3: {
          boxShadow: 'none',
          border: '1px solid #2a2a2a',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 4,
            backgroundColor: '#1a1a1a',
            '& fieldset': {
              borderColor: '#2a2a2a',
            },
            '&:hover fieldset': {
              borderColor: '#333333',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#16a34a',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#a0a0a0',
          },
          '& .MuiInputBase-input': {
            color: '#e5e5e5',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1a1a',
          '& fieldset': {
            borderColor: '#2a2a2a',
          },
          '&:hover fieldset': {
            borderColor: '#333333',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#16a34a',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1a1a',
        },
        icon: {
          color: '#a0a0a0',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 500,
          fontSize: '0.75rem',
        },
        outlined: {
          borderColor: '#2a2a2a',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          backgroundColor: '#0a0f0a',
          borderBottom: '1px solid #1a1a1a',
          backgroundImage: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid #1a1a1a',
          boxShadow: 'none',
          backgroundImage: 'none',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          margin: '2px 8px',
          '&:hover': {
            backgroundColor: 'rgba(22, 163, 74, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(22, 163, 74, 0.12)',
            borderLeft: '3px solid #16a34a',
            '&:hover': {
              backgroundColor: 'rgba(22, 163, 74, 0.16)',
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 36,
          color: 'inherit',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          color: 'inherit',
        },
        primary: {
          fontWeight: 500,
          fontSize: '0.85rem',
          color: 'inherit',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          border: '1px solid #1a1a1a',
          borderRadius: 4,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#0a0f0a',
          '& .MuiTableCell-head': {
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.7rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: '#a0a0a0',
            borderBottom: '1px solid #1a1a1a',
            padding: '10px 16px',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #1a1a1a',
          padding: '10px 16px',
          fontSize: '0.85rem',
          color: '#e5e5e5',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(22, 163, 74, 0.04)',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #1a1a1a',
        },
        indicator: {
          backgroundColor: '#16a34a',
          height: 2,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.8rem',
          fontWeight: 500,
          textTransform: 'none',
          color: '#a0a0a0',
          '&.Mui-selected': {
            color: '#16a34a',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#141414',
          border: '1px solid #2a2a2a',
          borderRadius: 4,
          backgroundImage: 'none',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '1rem',
          fontWeight: 600,
          borderBottom: '1px solid #1a1a1a',
          padding: '16px 24px',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#141414',
          border: '1px solid #2a2a2a',
          borderRadius: 4,
          backgroundImage: 'none',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.85rem',
          '&:hover': {
            backgroundColor: 'rgba(22, 163, 74, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(22, 163, 74, 0.12)',
            '&:hover': {
              backgroundColor: 'rgba(22, 163, 74, 0.16)',
            },
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          border: '1px solid',
        },
        standardSuccess: {
          backgroundColor: 'rgba(22, 163, 74, 0.1)',
          borderColor: 'rgba(22, 163, 74, 0.3)',
          color: '#22c55e',
        },
        standardError: {
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          borderColor: 'rgba(239, 68, 68, 0.3)',
          color: '#f87171',
        },
        standardWarning: {
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          borderColor: 'rgba(245, 158, 11, 0.3)',
          color: '#fbbf24',
        },
        standardInfo: {
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderColor: 'rgba(59, 130, 246, 0.3)',
          color: '#60a5fa',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1e1e1e',
          border: '1px solid #2a2a2a',
          borderRadius: 4,
          fontSize: '0.75rem',
          color: '#e5e5e5',
        },
        arrow: {
          color: '#1e1e1e',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#1a1a1a',
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          '& .MuiStepIcon-root': {
            color: '#333333',
            '&.Mui-active': {
              color: '#16a34a',
            },
            '&.Mui-completed': {
              color: '#22c55e',
            },
          },
          '& .MuiStepConnector-line': {
            borderColor: '#2a2a2a',
          },
        },
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          '& .MuiPaginationItem-root': {
            color: '#a0a0a0',
            borderColor: '#2a2a2a',
            '&.Mui-selected': {
              backgroundColor: '#16a34a',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#15803d',
              },
            },
            '&:hover': {
              backgroundColor: 'rgba(22, 163, 74, 0.08)',
            },
          },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        colorError: {
          backgroundColor: '#ef4444',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: '#16a34a',
            '& + .MuiSwitch-track': {
              backgroundColor: '#16a34a',
            },
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1a1a',
          borderRadius: 2,
        },
        bar: {
          borderRadius: 2,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: '#141414',
          border: '1px solid #1a1a1a',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(22, 163, 74, 0.08)',
          },
        },
      },
    },
  },
});

// Custom theme extensions
declare module '@mui/material/styles' {
  interface Theme {
    custom: {
      sidebar: {
        width: number;
        backgroundColor: string;
        textColor: string;
      };
      status: {
        active: string;
        pending: string;
        risk: string;
        warning: string;
        info: string;
      };
    };
  }

  interface ThemeOptions {
    custom?: {
      sidebar?: {
        width?: number;
        backgroundColor?: string;
        textColor?: string;
      };
      status?: {
        active?: string;
        pending?: string;
        risk?: string;
        warning?: string;
        info?: string;
      };
    };
  }
}

// Extend the theme with custom properties
theme.custom = {
  sidebar: {
    width: 260,
    backgroundColor: '#0a0f0a',
    textColor: '#a0a0a0',
  },
  status: {
    active: 'rgba(22, 163, 74, 0.15)',
    pending: 'rgba(245, 158, 11, 0.15)',
    risk: 'rgba(239, 68, 68, 0.15)',
    warning: 'rgba(245, 158, 11, 0.15)',
    info: 'rgba(59, 130, 246, 0.15)',
  },
};
